import { useCallback } from "react";

import { Action, Module, Run, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { hasAtLeastOneRequiredAction } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import useAacFlags from "hooks/useAacFlags";

import { isRunDiscardable } from "../useDiscardRun/accessValidation";
import useHasEntityWriteAccess from "./useHasEntityWriteAccess";

const DISCARD_ACCESS_TYPES = [Action.RunDiscard];

const hasDiscardAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(DISCARD_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can discard a run.
 * A user needs write access to discard a run.
 * A user can only discard a run when it's in a discardable state (Ready, Queued, or Unconfirmed).
 * @returns Function that takes a run and optional stack or module and returns a boolean indicating if user can discard it
 */
const useCanDiscardRun = (): ((
  run: Run,
  entity?: Partial<Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">>
) => boolean) => {
  const { customAccessRoles } = useAacFlags();
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (run: Run, entity?: Partial<Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">>) => {
      if (!isRunDiscardable(run)) {
        return false;
      }

      if (!entity) {
        return false;
      }

      const hasAccess =
        hasEntityWriteAccess(entity) ||
        (customAccessRoles && hasDiscardAccess(viewer, entity.spaceDetails?.id || entity.space));

      if (hasAccess) {
        return true;
      }

      return false;
    },
    [viewer, hasEntityWriteAccess, customAccessRoles]
  );
};

export default useCanDiscardRun;
